import { useNavigate } from "react-router-dom";
import { DashboardTile } from "../components/DashboardTile";
import { Button } from "@/components/ui/button";
import BlurredLightEffect from "@common/components/BlurredLightEffect";
import { useRecoilValue } from "recoil";
import { badgeNumberState } from "@common/state/badgeNumberState";
import { needsAction, startKYC } from "@common/state/organizationState";

interface InfoTileContentProps {
  title: string;
  message: string;
  buttonText: string;
  onClick: () => void;
}

const InfoTileContent: React.FC<InfoTileContentProps> = ({
  title,
  message,
  buttonText,
  onClick,
}) => (
  <div className="flex h-full max-w-[400px] flex-col justify-center">
    <div className="whitespace-pre-line font-serif text-3xl">{title}</div>
    <div className="mt-2 text-slate">{message}</div>
    <Button className="mt-8 w-[200px]" onClick={onClick}>
      {buttonText}
    </Button>
  </div>
);

export const OverviewTile = () => {
  const navigate = useNavigate();
  const badge = useRecoilValue(badgeNumberState);
  const showAdminStipend = badge.admin.stipends > badge.admin.reports;
  const isActionRequired = useRecoilValue(needsAction);
  const canStartKYC = useRecoilValue(startKYC);

  const getContent = () => {
    if (canStartKYC) {
      return (
        <InfoTileContent
          title={`Bank account \nsetup.`}
          message="Let's get started with setting up your new bank account."
          buttonText="Manage Account"
          onClick={() => navigate("/admin/settings/bank-account")}
        />
      );
    }

    if (isActionRequired) {
      return (
        <InfoTileContent
          title={`Additional Information \nrequired.`}
          message="More information is required to complete your setup."
          buttonText="Manage Account"
          onClick={() => navigate("/admin/settings/bank-account")}
        />
      );
    }

    if (showAdminStipend) {
      return (
        <InfoTileContent
          title={`Stipends \nawaiting review.`}
          message="Employees are waiting to be reimbursed."
          buttonText="Review Stipends"
          onClick={() => navigate("/admin/stipends/pending")}
        />
      );
    }

    return (
      <InfoTileContent
        title={`Expense reports \nawaiting review.`}
        message="Employees are waiting to be reimbursed."
        buttonText="Review Reports"
        onClick={() => navigate("/admin/reports/pending")}
      />
    );
  };

  return (
    <DashboardTile className="relative overflow-hidden">
      <BlurredLightEffect variant="top-left" />
      {getContent()}
    </DashboardTile>
  );
};
